import { Component, Input, OnChanges, output } from '@angular/core';
import {
  SelectItemV2,
  RadioGroupOptionV2,
  findItemInList,
  InputV2Module,
  RadioV2Module,
  SelectV2Module,
} from '@schenck/rotec-components';
import { DayOfWeek, DayOfWeekAppearanceInMonth } from 'src/api';
import { TranslateDirective } from '@ngx-translate/core';

export interface SchedulingMonthlyDayModel {
  isExact: false;
  interval: number;
  weekOfMonth: DayOfWeekAppearanceInMonth;
  dayOfWeek: DayOfWeek;
}

export interface SchedulingMonthlyExactModel {
  isExact: true;
  dayOfMonth: number;
  interval: number;
}

export type SchedulingMonthlyModel = SchedulingMonthlyExactModel | SchedulingMonthlyDayModel;

@Component({
  selector: 'ma-scheduling-monthly',
  templateUrl: './scheduling-monthly.component.html',
  styleUrls: ['./scheduling-monthly.component.less', '../scheduling-interval.less'],
  imports: [TranslateDirective, InputV2Module, RadioV2Module, SelectV2Module],
})
export class SchedulingMonthlyComponent implements OnChanges {
  @Input()
  public scheduling: SchedulingMonthlyModel;
  public readonly schedulingChange = output<SchedulingMonthlyModel>();

  public isIntervalExactItems: RadioGroupOptionV2<boolean>[] = [
    {
      value: true,
      name: 'Exactly at',
    },
    {
      value: false,
      name: 'At',
    },
  ];

  public weekOfMonthOptions: SelectItemV2<DayOfWeekAppearanceInMonth>[] = [
    { value: DayOfWeekAppearanceInMonth.First, name: DayOfWeekAppearanceInMonth.First },
    { value: DayOfWeekAppearanceInMonth.Second, name: DayOfWeekAppearanceInMonth.Second },
    { value: DayOfWeekAppearanceInMonth.Third, name: DayOfWeekAppearanceInMonth.Third },
    { value: DayOfWeekAppearanceInMonth.Fourth, name: DayOfWeekAppearanceInMonth.Fourth },
    { value: DayOfWeekAppearanceInMonth.Last, name: DayOfWeekAppearanceInMonth.Last },
  ];

  public dayOfWeekOptions: SelectItemV2<DayOfWeek>[] = [
    { value: DayOfWeek.Monday, name: DayOfWeek.Monday },
    { value: DayOfWeek.Tuesday, name: DayOfWeek.Tuesday },
    { value: DayOfWeek.Wednesday, name: DayOfWeek.Wednesday },
    { value: DayOfWeek.Thursday, name: DayOfWeek.Thursday },
    { value: DayOfWeek.Friday, name: DayOfWeek.Friday },
    { value: DayOfWeek.Saturday, name: DayOfWeek.Saturday },
    { value: DayOfWeek.Sunday, name: DayOfWeek.Sunday },
  ];

  // =========== interval
  public interval = 1;

  public intervalChange(interval: string): void {
    this.interval = Number(interval);
    this.emitSchedulingChange();
  }

  public isIntervalExact = true;

  public isIntervalExactChange(isIntervalExact: boolean): void {
    this.isIntervalExact = isIntervalExact;
    this.emitSchedulingChange();
  }

  // =========== exactly at
  public dayOfMonth = 1;

  public dayOfMonthChange(dayOfMonth: string): void {
    this.dayOfMonth = Number(dayOfMonth);
    this.emitSchedulingChange();
  }

  // =========== at x'th day of month
  public dayOfWeek = this.dayOfWeekOptions[0];

  public dayOfWeekChange(dayOfWeek: SelectItemV2<DayOfWeek>): void {
    this.dayOfWeek = dayOfWeek;
    this.emitSchedulingChange();
  }

  public weekOfMonth = this.weekOfMonthOptions[0];

  public weekOfMonthChange(weekOfMonth: SelectItemV2<DayOfWeekAppearanceInMonth>): void {
    this.weekOfMonth = weekOfMonth;
    this.emitSchedulingChange();
  }

  // =========== scheduling changes

  public ngOnChanges(): void {
    if (!this.scheduling) {
      return;
    }

    this.interval = this.scheduling.interval;

    if (this.scheduling.isExact) {
      this.dayOfMonth = this.scheduling.dayOfMonth;
    } else {
      const scheduling = <SchedulingMonthlyDayModel>this.scheduling;
      this.weekOfMonth = findItemInList(this.weekOfMonthOptions, scheduling.weekOfMonth);
      this.dayOfWeek = findItemInList(this.dayOfWeekOptions, scheduling.dayOfWeek);
    }
  }

  private emitSchedulingChange(): void {
    if (this.isIntervalExact) {
      this.emitExactSchedulingChange();
    } else {
      this.emitDaySchedulingChange();
    }
  }

  private emitDaySchedulingChange(): void {
    this.schedulingChange.emit({
      isExact: false,
      interval: this.interval,
      dayOfWeek: <DayOfWeek>this.dayOfWeek.value,
      weekOfMonth: <DayOfWeekAppearanceInMonth>this.weekOfMonth.value,
    });
  }

  private emitExactSchedulingChange(): void {
    this.schedulingChange.emit({
      isExact: true,
      interval: this.interval,
      dayOfMonth: this.dayOfMonth,
    });
  }
}
